import React from 'react';
import {
  TvIcon,
  HandThumbUpIcon,
  GlobeAmericasIcon,
  AcademicCapIcon,
  ArrowUpRightIcon,
} from '@heroicons/react/24/outline';
import Sponsors from '../../assets/we-elevate-logo.svg';
import HeroImage from '../../assets/Hero-Image.png';

export default function Hero() {
  return (
    <>
      {/* Hero Section */}
      <section className="min-h-screen w-full bg-gradient-to-br from-pink-50 via-white to-purple-50 overflow-hidden flex flex-col items-center justify-center px-4 sm:px-6 md:px-10 lg:px-20 py-16 text-center">
        {/* Main Heading */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
          WE-Elevate is here in <br />
          <span className="text-primary font-serif italic">Nigeria</span>
        </h1>

        {/* Description */}
        <p className=" text-base sm:text-lg md:text-xl max-w-2xl md:max-w-3xl mb-8">
          Empowering women-led MSMEs with the skills, tools, and opportunities
          to scale globally.{' '}
          <span className="font-semibold">
            Applications now open — limited slots available.
          </span>
        </p>

        {/* CTA Button */}
        <a
          href="https://bit.ly/WE-ElevateNigeria"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Apply for WE-Elevate Nigeria (opens in a new tab)"
          className="group inline-flex items-center gap-2 bg-black text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium mb-12 hover:bg-gray-900 transition-colors"
        >
          Apply Now
          <div className="rounded-full bg-[#274892] p-1 group-hover:bg-[#1f3b7a] transition-colors">
            <ArrowUpRightIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-300 group-hover:text-white" />
          </div>
        </a>

        {/* Sponsors Section */}
        <div className="w-full max-w-2xl sm:max-w-full mx-auto mb-12">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-6">
            Proudly sponsored by
          </h3>
          <img
            src={Sponsors}
            alt="Sponsors: Digital Cooperation Organization, NCC, NITDA, and ONDI"
            className="w-full h-auto px-4 sm:px-0 object-contain"
          />
        </div>

        {/* Hero Image */}
        <div className="w-full max-w-xl md:max-w-2xl lg:max-w-7xl mx-auto">
          <img
            src={HeroImage}
            alt="Hero-Image"
            className="w-full h-auto object-cover"
          />
        </div>
      </section>

      {/* What you need to know Section */}
      <section className="w-full flex flex-col items-center justify-center px-6 sm:px-10 lg:px-20 bg-white text-center">
        <div className="bg-[#274892] text-white rounded-3xl p-8 sm:p-12 max-w-7xl shadow-md">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
            What you need to know about this program
          </h2>
          <p className="text-base sm:text-lg leading-relaxed mb-4">
            WE-Elevate is a capacity-building program equipping female
            entrepreneurs with digital, financial, and entrepreneurial skills.
            It combines online learning modules, mentorship, and access to
            global e-commerce trading platforms to help women-led MSMEs
            transition from offline to online operations.
          </p>
          <p className="text-base sm:text-lg leading-relaxed">
            Through tailored, step-by-step training, participants progress from
            being e-commerce aware to e-commerce scalable, ultimately
            formalizing their businesses, expanding their customer bases, and
            creating sustainable jobs.
          </p>
        </div>
      </section>

      {/* What you'll gain Section */}
      <section className="w-full bg-gray-50 px-6 sm:px-10 lg:px-20 mt-10 text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-12">
          What you’ll gain
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Card 1 */}
          <div className="bg-[#FEF6FA] border border-primary rounded-2xl p-6 text-left">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#FDE8F3] mb-4">
              <TvIcon className="h-6 w-6 text-primary" />
            </div>

            <h3 className="text-lg font-semibold mb-2">
              Digital Skills Training
            </h3>
            <p className="text-gray-600 text-sm sm:text-base">
              Learn practical, hands-on digital tools—from social media
              marketing to online payments and e-commerce operations—so you can
              confidently run your business in today’s digital-first world.
            </p>
          </div>

          {/* Card 2 */}
          <div className="bg-[#F5FFFA] border border-[#006837] rounded-2xl p-6 text-left">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#F0FFF8] mb-4">
              <HandThumbUpIcon className="h-6 w-6 text-[#006837]" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Global Market Access</h3>
            <p className="text-gray-600 text-sm sm:text-base">
              Go beyond local sales. Gain exposure to global e-commerce
              platforms that connect you with customers across Africa and
              beyond, opening doors to new markets and bigger opportunities.
            </p>
          </div>

          {/* Card 3 */}
          <div className="bg-[#FFF8F5] border border-[#FF7733] rounded-2xl p-6 text-left">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#FFEEE5] mb-4">
              <GlobeAmericasIcon className="h-6 w-6 text-[#FF7733]" />
            </div>

            <h3 className="text-lg font-semibold mb-2">Business Growth</h3>
            <p className="text-gray-600 text-sm sm:text-base">
              Turn knowledge into results. Expand your customer base, build
              stronger revenue streams, and create sustainable jobs that impact
              your community while securing your financial independence.
            </p>
          </div>

          {/* Card 4 */}
          <div className="bg-[#FBF5F4] border border-[#BC6248] rounded-2xl p-6 text-left shadow-sm hover:shadow-md transition">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[#F8EFEC] mb-4">
              <AcademicCapIcon className="h-6 w-6 text-[#BC6248]" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Mentorship</h3>
            <p className="text-gray-600 text-sm sm:text-base">
              Get guidance from seasoned entrepreneurs, business experts, and
              industry leaders who share real-world insights to help you avoid
              mistakes and scale faster.
            </p>
          </div>
        </div>
      </section>

      {/* Apply Now Section */}
      <section className="w-full flex flex-col items-center justify-center py-10 px-6 mt-10">
        <div
          className="w-full max-w-7xl bg-[#76bb7a] text-white rounded-xl py-16 px-8 sm:px-12 text-center shadow-lg"
          style={{
            backgroundImage:
              "url('https://shecluded-medias.s3.us-east-2.amazonaws.com/bg-loan.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: '180px',
          }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Let's create a lasting impact together.
          </h2>
          <p className="text-xl sm:text-xl md:text-2xl font-medium leading-relaxed mb-8 max-w-2xl sm:max-w-3xl mx-auto text-center">
            Empower the future of women-led businesses through digital
            inclusion. By partnering with WE-Elevate, your organization
            contributes to a movement helping thousands of women transition from
            offline to online commerce
          </p>
          {/* <a
            href="https://bit.ly/WE-ElevateNigeria"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Apply for WE-Elevate Nigeria (opens in a new tab)"
            className="inline-block bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition"
          >
            Partner with us
          </a> */}
          <a
            href="mailto:hello@shecluded.com?subject=WE-Elevate Partnership Inquiry"
            aria-label="Contact us about WE-Elevate partnership"
            className="inline-block bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition"
          >
            Partner with us
          </a>
        </div>
      </section>
    </>
  );
}
